import { addInlineStyle } from "src/addStyles";
import style from "./outgoing.scss?inline";
import { memo, useState } from "react";
import FilePreview from "../ChatInput/FilePreview";
import clsx from "clsx";
import { MESSAGE_GUTTER } from ".";
import IconChevronDown from "src/assets/SvgIcons/IconChevronDown";
import IconButton from "src/components/shared/Buttons/IconButton";
import GooeyTooltip from "src/components/shared/Tooltip";
addInlineStyle(style);

interface ButtonPressed {
  button_id: string;
  context_msg_id: string;
  button_title?: string | null;
}

interface OutgoingMsgProps {
  input_prompt?: string;
  input_audio?: Blob | string | string[];
  input_images?: string[];
  input_documents?: string[];
  button_pressed?: ButtonPressed;
  input_location?: {
    latitude?: number;
    longitude?: number;
  };
}

const OutgoingMsg = memo(
  ({
    input_prompt = "",
    input_audio = undefined,
    button_pressed = undefined,
    input_location: { latitude, longitude } = {},
    input_images = [],
    input_documents = [],
  }: OutgoingMsgProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    let mapUrl;
    if (latitude && longitude) {
      mapUrl = buildOpenStreetMapEmbedUrl(latitude, longitude);
    } else {
      input_prompt ||= button_pressed?.button_title || "";
    }

    // Determine if message is large (more than 200 characters or more than 5 lines)
    const isLargeMessage =
      input_prompt &&
      (input_prompt.length > 200 || input_prompt.split("\n").length > 5);

    // Truncate text for collapsed view (first 200 characters or first 3 lines)
    const getTruncatedText = (text: string): string => {
      const lines = text.split("\n");
      // If more than 3 lines, show first 3 lines with ellipsis
      if (lines.length > 3) {
        const firstThreeLines = lines.slice(0, 3).join("\n");
        // Make sure we don't exceed 200 chars even with line breaks
        if (firstThreeLines.length > 200) {
          return firstThreeLines.slice(0, 200).trim() + "...";
        }
        return firstThreeLines + "...";
      }
      // If single line or few lines but long text, truncate to 200 chars
      if (text.length > 200) {
        // Try to break at word boundary if possible
        const truncated = text.slice(0, 200);
        const lastSpace = truncated.lastIndexOf(" ");
        // If we find a space near the end (within last 30 chars), break there
        if (lastSpace > 170) {
          return truncated.slice(0, lastSpace) + "...";
        }
        return truncated.trim() + "...";
      }
      return text;
    };

    const displayText =
      isLargeMessage && !isExpanded
        ? getTruncatedText(input_prompt)
        : input_prompt;

    const input_audio_url = resolveInputAudioUrl(input_audio);

    return (
      <div className="d-flex flex-col align-end">
        <div className="gooey-outgoingMsg gmb-24 d-flex flex-col align-end">
          {input_images && input_images.length > 0 && (
            <div
              className={clsx(
                `gpr-${MESSAGE_GUTTER}`,
                input_prompt && "gmt-16",
              )}
            >
              <FilePreview
                files={input_images.map((url: string) => ({
                  url,
                  title: url,
                  refNumber: "",
                  type: "image",
                }))}
              />
            </div>
          )}
          {input_documents.length > 0 && (
            <div
              className={clsx(
                `gpr-${MESSAGE_GUTTER}`,
                input_prompt && "gmt-16",
              )}
            >
              <FilePreview
                files={input_documents.map((url: string) => ({
                  url,
                  title: url,
                  refNumber: "",
                }))}
              />
            </div>
          )}
          {input_audio_url && (
            <div
              className={clsx(
                `gpr-${MESSAGE_GUTTER}`,
                input_prompt && "gmt-16",
              )}
            >
              <AudioPlayer url={input_audio_url} />
            </div>
          )}
          {input_prompt && (
            <div
              className={clsx(
                "d-flex align-start pos-relative gooey-outgoing-text br-large gp-8 gmt-8",
                `gmr-${MESSAGE_GUTTER}`,
              )}
            >
              <p
                className={clsx(
                  "font_16_400 gp-4 overflow-hidden",
                  isLargeMessage &&
                    !isExpanded &&
                    "gooey-outgoing-text-collapsed",
                )}
              >
                {displayText}
              </p>
              {isLargeMessage && (
                <div>
                  <GooeyTooltip
                    text={isExpanded ? "Collapse message" : "Expand message"}
                  >
                    <IconButton
                      className="gooey-outgoing-expand-btn gmt-4"
                      variant="text"
                      onClick={() => setIsExpanded(!isExpanded)}
                    >
                      <IconChevronDown
                        size={16}
                        style={{
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      />
                    </IconButton>
                  </GooeyTooltip>
                </div>
              )}
            </div>
          )}
          {latitude && longitude && (
            <div
              className={clsx(
                `gpr-${MESSAGE_GUTTER}`,
                input_prompt && "gmt-16",
              )}
            >
              <iframe
                width="100%"
                height="200px"
                src={mapUrl}
                loading="lazy"
                style={{
                  border: "1px solid #ddd",
                  aspectRatio: "16/9",
                  borderRadius: "8px",
                }}
              ></iframe>
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default OutgoingMsg;

function resolveInputAudioUrl(
  input_audio?: Blob | string | string[],
): string | undefined {
  if (!input_audio) return undefined;
  if (Array.isArray(input_audio)) return input_audio[0];
  if (typeof input_audio === "string") return input_audio;
  return (URL || webkitURL).createObjectURL(input_audio as Blob);
}

function AudioPlayer({ url }: { url: string }) {
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/?#]+)/);
  if (driveMatch) {
    const [loaded, setLoaded] = useState(false);
    return (
      <div style={{ position: "relative", width: 300, height: 80 }}>
        {!loaded && (
          <p
            className="font_14_400 text-muted"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            Loading audio...
          </p>
        )}
        <iframe
          src={`https://drive.google.com/file/d/${driveMatch[1]}/preview`}
          width="300"
          height="80"
          allow="autoplay"
          onLoad={() => setLoaded(true)}
          style={{
            border: "none",
            borderRadius: "8px",
            opacity: loaded ? 1 : 0,
          }}
        />
      </div>
    );
  }
  return <audio controls src={url}></audio>;
}

function buildOpenStreetMapEmbedUrl(
  latitude: number,
  longitude: number,
): string {
  const coords = calculateBoundingBox(latitude, longitude);
  const bbox = coords.join(",");
  const url = new URL("https://www.openstreetmap.org/export/embed.html");
  url.searchParams.set("bbox", bbox);
  url.searchParams.set("layer", "mapnik");
  url.searchParams.set("marker", [latitude, longitude].join(","));
  return url.toString();
}

function calculateBoundingBox(
  lat: number,
  lon: number,
  zoom: number = 16,
): [number, number, number, number] {
  const degreesPerTile = 360 / Math.pow(2, zoom);
  const halfTile = degreesPerTile / 2;

  const minLon = lon - halfTile;
  const maxLon = lon + halfTile;
  const minLat = lat - halfTile;
  const maxLat = lat + halfTile;

  return [minLon, minLat, maxLon, maxLat];
}
